import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

// One <audio> element for the whole app. Clips seek in code after metadata
// loads rather than via #t= media fragments, which iOS Safari handles
// inconsistently, and play() is called inside the tap handler so mobile
// autoplay rules allow it.

export interface Clip {
  key: string
  src: string
  start: number
  // Pause here; null plays on (whole-episode playback).
  end: number | null
  // Seek to start even if this clip is already playing or paused mid-way
  // (the episode scrubber's markers).
  restart?: boolean
}

interface PlayerState {
  key: string | null
  src: string | null
  playing: boolean
  time: number
  error: string | null
}

interface PlayerApi extends PlayerState {
  play: (clip: Clip) => void
  stop: () => void
}

const PlayerContext = createContext<PlayerApi | null>(null)

export function PlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const clipRef = useRef<Clip | null>(null)
  // Where to seek once metadata arrives; the latest tap wins.
  const pendingSeek = useRef<number | null>(null)
  const [state, setState] = useState<PlayerState>({ key: null, src: null, playing: false, time: 0, error: null })

  const play = useCallback((clip: Clip) => {
    const a = audioRef.current
    if (!a) return
    const current = clipRef.current
    const sameClip = current?.key === clip.key && current.src === clip.src

    if (sameClip && !a.paused && !clip.restart) {
      a.pause()
      return
    }
    // Resume a paused clip where it stopped, unless it already reached its end.
    const resumable = !clip.restart && sameClip && a.currentTime >= clip.start && (clip.end == null || a.currentTime < clip.end - 0.5)

    clipRef.current = clip
    setState({ key: clip.key, src: clip.src, playing: true, time: resumable ? a.currentTime : clip.start, error: null })

    if (!resumable) {
      if (a.dataset.src !== clip.src) {
        a.dataset.src = clip.src
        a.src = clip.src
      }
      if (a.readyState >= HTMLMediaElement.HAVE_METADATA) a.currentTime = clip.start
      else pendingSeek.current = clip.start
    }
    a.play().catch((err: Error) => {
      if (err.name === 'AbortError') return
      setState((s) => ({ ...s, playing: false, error: 'This episode’s audio couldn’t be played.' }))
    })
  }, [])

  const stop = useCallback(() => {
    audioRef.current?.pause()
  }, [])

  useEffect(() => {
    const a = audioRef.current
    if (!a) return
    const onTime = () => {
      const clip = clipRef.current
      if (clip?.end != null && a.currentTime >= clip.end) a.pause()
      setState((s) => ({ ...s, time: a.currentTime }))
    }
    const onMetadata = () => {
      if (pendingSeek.current != null) a.currentTime = pendingSeek.current
      pendingSeek.current = null
    }
    const onPlay = () => setState((s) => ({ ...s, playing: true }))
    const onPause = () => setState((s) => ({ ...s, playing: false }))
    const onError = () => setState((s) => ({ ...s, playing: false, error: 'This episode’s audio couldn’t be loaded.' }))
    a.addEventListener('loadedmetadata', onMetadata)
    a.addEventListener('timeupdate', onTime)
    a.addEventListener('play', onPlay)
    a.addEventListener('pause', onPause)
    a.addEventListener('error', onError)
    return () => {
      a.removeEventListener('loadedmetadata', onMetadata)
      a.removeEventListener('timeupdate', onTime)
      a.removeEventListener('play', onPlay)
      a.removeEventListener('pause', onPause)
      a.removeEventListener('error', onError)
    }
  }, [])

  const value = useMemo(() => ({ ...state, play, stop }), [state, play, stop])
  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="none" data-testid="player" />
    </PlayerContext.Provider>
  )
}

export function usePlayer(): PlayerApi {
  const ctx = useContext(PlayerContext)
  if (!ctx) throw new Error('usePlayer outside PlayerProvider')
  return ctx
}

// Clip length shown on the Play button, e.g. "0:47".
export function clipLength(start: number, end: number | null): string | null {
  if (end == null || end <= start) return null
  const s = Math.round(end - start)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
